import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Phone, ShieldCheck } from "lucide-react";

interface PhoneVerificationProps {
  phone: string;
  onPhoneChange: (phone: string) => void;
  onVerified: (verified: boolean) => void;
  isVerified: boolean;
}

export function PhoneVerification({ phone, onPhoneChange, onVerified, isVerified }: PhoneVerificationProps) {
  const [isSending, setIsSending] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");

  const formatPhoneDisplay = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 10);
    onPhoneChange(raw);
    // Reset verification if phone changes
    if (codeSent || isVerified) {
      setCodeSent(false);
      setOtpCode("");
      onVerified(false);
    }
  };

  const sendCode = async () => {
    if (phone.length !== 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }
    setIsSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("twilio-verify", {
        body: { action: "send", phone: `+1${phone}` },
      });
      if (error) throw error;
      if (!data.success) {
        toast.error(data.error || "Failed to send code");
        return;
      }
      setCodeSent(true);
      toast.success("Verification code sent!");
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to send verification code");
    } finally {
      setIsSending(false);
    }
  };

  const checkCode = async () => {
    if (otpCode.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }
    setIsChecking(true);
    try {
      const { data, error } = await supabase.functions.invoke("twilio-verify", {
        body: { action: "check", phone: `+1${phone}`, code: otpCode },
      });
      if (error) throw error;
      if (data.verified) {
        onVerified(true);
        toast.success("Phone number verified!");
      } else {
        toast.error("Invalid code. Please try again.");
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Verification failed");
    } finally {
      setIsChecking(false);
    }
  };

  if (isVerified) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Phone Number</span>
        </div>
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
          <span className="text-sm font-medium text-green-800">+1 {formatPhoneDisplay(phone)}</span>
          <Badge className="bg-green-100 text-green-700 border-green-200 ml-auto text-xs">
            <ShieldCheck className="h-3 w-3 mr-1" /> Verified
          </Badge>
        </div>
        <button
          type="button"
          onClick={() => { onPhoneChange(""); onVerified(false); setCodeSent(false); setOtpCode(""); }}
          className="text-xs text-primary hover:underline"
        >
          Use a different number
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Phone className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Phone Number <span className="text-red-500">*</span></span>
      </div>
      <p className="text-xs text-muted-foreground">
        We only accept Canadian phone numbers. A verification code will be sent via SMS.
      </p>

      <div className="flex gap-2">
        <div className="flex items-center bg-muted px-3 rounded-l-md border border-r-0 text-sm font-medium text-muted-foreground">
          +1
        </div>
        <Input
          value={formatPhoneDisplay(phone)}
          onChange={handlePhoneInput}
          placeholder="(416) 555-1234"
          className="rounded-l-none flex-1"
          disabled={codeSent}
          maxLength={14}
        />
        {!codeSent && (
          <Button
            type="button"
            onClick={sendCode}
            disabled={phone.length !== 10 || isSending}
            size="sm"
            className="whitespace-nowrap"
          >
            {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Code"}
          </Button>
        )}
      </div>

      {codeSent && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Enter the 6-digit code sent to +1 {formatPhoneDisplay(phone)}
          </p>
          <div className="flex gap-2">
            <Input
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="text-center text-lg tracking-widest font-mono"
              maxLength={6}
              autoFocus
            />
            <Button
              type="button"
              onClick={checkCode}
              disabled={otpCode.length !== 6 || isChecking}
              size="sm"
              className="whitespace-nowrap"
            >
              {isChecking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
            </Button>
          </div>
          <button
            type="button"
            onClick={() => { setCodeSent(false); setOtpCode(""); }}
            className="text-xs text-primary hover:underline"
          >
            Change number or resend code
          </button>
        </div>
      )}
    </div>
  );
}
